import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Lock, Copy, Download, Shield, ExternalLink, X, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import logo from "@/assets/logo.png";
import { SocialLinks } from "@/components/SocialLinks";
import html2canvas from "html2canvas";

interface LibraryItem {
  id: string;
  title: string | null;
  category: string | null;
  toxicity_score: number;
  teaser_text: string | null;
  analysis_logic: string | null;
  neutralization_script: string | null;
  invideo_prompt: string | null;
}

const getScoreColor = (score: number) => {
  if (score >= 7) return "hsl(25, 95%, 53%)";
  if (score >= 4) return "hsl(38, 92%, 50%)";
  return "hsl(152, 69%, 51%)";
};

const CreatorPortal = () => {
  const { promoCode } = useParams<{ promoCode: string }>();
  const code = (promoCode || "").toUpperCase();
  const [redemptionCount, setRedemptionCount] = useState<number>(0);
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const vaultRef = useRef<HTMLDivElement>(null);
  const analyzeRef = useRef<HTMLDivElement>(null);

  const scrollToAnalyze = () => {
    analyzeRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const { data: promoData } = await supabase
        .from("promo_codes")
        .select("id, current_uses")
        .eq("code", code)
        .maybeSingle();

      if (promoData) {
        setRedemptionCount(promoData.current_uses || 0);
      }

      const { data: libraryData } = await supabase
        .from("content_library")
        .select("id, title, category, toxicity_score, teaser_text, analysis_logic, neutralization_script, invideo_prompt")
        .eq("workflow_status", "published")
        .is("user_id", null)
        .order("created_at", { ascending: false });

      if (libraryData) {
        setItems(libraryData);
      }

      setLoading(false);
    };

    fetchData();
  }, [code]);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${label} copied!` });
    } catch {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };

  const downloadTeaserCard = async (item: LibraryItem, index: number) => {
    const el = document.getElementById(`teaser-card-${index}`);
    if (!el) return;
    try {
      const canvas = await html2canvas(el, { backgroundColor: "#0a0a0a", scale: 2 });
      const link = document.createElement("a");
      link.download = `${item.title || "teaser"}-card.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast({ title: "Card downloaded!" });
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  };

  const getHookLine = (item: LibraryItem): string | null => {
    return item.invideo_prompt || item.teaser_text?.split(".")[0] || null;
  };

  const scrollToVault = () => {
    vaultRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast({ title: "Please fill in all fields", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await supabase.functions.invoke("send-email", {
        body: {
          to: "support@greyrockai.app",
          subject: `Creator Partnership — ${contactForm.name}`,
          text: `From: ${contactForm.name} (${contactForm.email})\nCode: ${code}\n\n${contactForm.message}`,
        },
      });
      setContactOpen(false);
      setContactForm({ name: "", email: "", message: "" });
      toast({ title: "Message sent!", description: "We'll get back to you soon." });
    } catch {
      toast({ title: "Failed to send", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading portal...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <nav className="sticky top-0 z-20 flex items-center justify-between px-6 py-4 md:px-12 border-b border-border bg-background/60 backdrop-blur-xl">
        <Link to="/" className="flex items-center gap-3">
          <img src={logo} alt="GreyRock AI®" className="w-10 h-10 object-contain drop-shadow-[0_0_12px_rgba(0,242,255,0.3)]" />
          <span className="text-xl font-bold tracking-tight">
            <span className="text-foreground">Grey</span>
            <span className="text-cyan">Rock</span>
            <span className="text-foreground"> AI</span>
            <sup className="text-[10px] text-muted-foreground">®</sup>
          </span>
        </Link>
        <Link to="/" className="px-4 py-2 text-sm font-medium text-cyan hover:text-cyan/80 transition-colors flex items-center gap-1">
          Open App <ExternalLink className="w-3 h-3" />
        </Link>
      </nav>

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Compact Welcome + Quick-Start */}
        <section className="py-6 border-b border-border">
          <h2 className="text-lg md:text-xl font-bold tracking-tight text-foreground text-center mb-1">
            Welcome to the Lab.
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-4">
            Your insights move the needle. Our data proves the pattern.
          </p>

          {/* Horizontal Step Bar */}
          <div className="flex items-stretch gap-3 justify-center">
            <button
              onClick={scrollToAnalyze}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card hover:border-cyan/40 hover:bg-cyan/5 transition-colors group cursor-pointer"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan/10 text-cyan font-bold text-xs font-mono group-hover:bg-cyan/20 transition-colors">1</span>
              <span className="text-xs font-medium text-foreground">Analyze</span>
            </button>
            <button
              onClick={scrollToVault}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card hover:border-cyan/40 hover:bg-cyan/5 transition-colors group cursor-pointer"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan/10 text-cyan font-bold text-xs font-mono group-hover:bg-cyan/20 transition-colors">2</span>
              <span className="text-xs font-medium text-foreground">Export</span>
            </button>
            <button
              onClick={() => setContactOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card hover:border-cyan/40 hover:bg-cyan/5 transition-colors group cursor-pointer"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan/10 text-cyan font-bold text-xs font-mono group-hover:bg-cyan/20 transition-colors">3</span>
              <span className="text-xs font-medium text-foreground">Share</span>
            </button>
          </div>
        </section>

        {/* Your Code + Impact */}
        <section className="py-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            <span className="text-foreground">Your Code: </span>
            <span className="text-cyan font-mono">{code}</span>
          </h1>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-cyan/20 bg-cyan/5">
              <Shield className="w-5 h-5 text-cyan" />
              <span className="text-2xl font-bold text-foreground">{redemptionCount}</span>
              <span className="text-sm text-muted-foreground">
                {redemptionCount === 1 ? "Shield" : "Shields"} Activated via <span className="text-cyan font-mono">{code}</span>
              </span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3 max-w-md mx-auto">
            Share your code with your audience. When they redeem it, they get 5 free credits — and your impact count grows.
          </p>
          <button
            onClick={() => setContactOpen(true)}
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan hover:text-cyan/80 transition-colors font-medium"
          >
            <Send className="w-3 h-3" /> Message Support — Creator Partnerships
          </button>
        </section>

        {/* Asset Vault */}
        <section className="pb-16" ref={vaultRef}>
          <div className="flex items-center justify-between mb-8 border-b border-border pb-4">
            <h2 className="text-lg font-mono uppercase tracking-[0.15em] text-foreground">Forensic Asset Vault</h2>
            <Badge variant="outline" className="text-muted-foreground border-border text-xs font-mono">
              {items.length} {items.length === 1 ? "pattern" : "patterns"}
            </Badge>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-xl">
              <p className="text-muted-foreground">No published patterns yet. Check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10 justify-items-center">
              {items.map((item, i) => {
                const hookLine = getHookLine(item);
                const scoreColor = getScoreColor(item.toxicity_score);

                return (
                  <div key={item.id} className="flex flex-col items-center gap-4 w-full max-w-[380px]">
                    {/* ── 9:16 FORENSIC REPORT CARD ── */}
                    <div
                      id={`teaser-card-${i}`}
                      className="relative w-full overflow-hidden flex flex-col"
                      style={{
                        background: "#0a0a0a",
                        aspectRatio: "9 / 16",
                        border: "1px solid hsl(0 0% 18%)",
                        borderRadius: "8px",
                      }}
                    >
                      {/* ▸ Header Row */}
                      <div
                        className="flex-shrink-0 flex items-center justify-between px-5 py-4"
                        style={{ borderBottom: "1px solid hsl(0 0% 14%)" }}
                      >
                        <span className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "hsl(0 0% 40%)" }}>
                          GREYROCKAI®
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "hsl(0 0% 32%)" }}>
                          FORENSIC ANALYSIS
                        </span>
                      </div>

                      {/* ▸ Hook Quote */}
                      {hookLine && (
                        <div className="flex-shrink-0 px-6 py-5 text-center" style={{ borderBottom: "1px solid hsl(0 0% 12%)" }}>
                          <p className="text-[13px] leading-relaxed italic font-light tracking-wide" style={{ color: "hsl(184 50% 50%)" }}>
                            "{hookLine}"
                          </p>
                        </div>
                      )}

                      <div className="flex-shrink-0 w-full" style={{ height: "1px", background: "hsl(0 0% 10%)" }} />

                      {/* ▸ Title + Score */}
                      <div className="flex-shrink-0 flex flex-col items-center gap-3 px-6 py-6" style={{ borderBottom: "1px solid hsl(0 0% 12%)" }}>
                        <h3 className="text-2xl font-black text-center leading-tight tracking-tight uppercase" style={{ color: "hsl(0 0% 93%)" }}>
                          {item.title || "Untitled Pattern"}
                        </h3>
                        {item.category && (
                          <span className="text-[9px] font-mono uppercase tracking-[0.25em]" style={{ color: "hsl(0 0% 38%)" }}>
                            {item.category}
                          </span>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] font-mono uppercase tracking-[0.15em]" style={{ color: "hsl(0 0% 45%)" }}>
                            Forensic Score:
                          </span>
                          <span className="text-xl font-black font-mono" style={{ color: scoreColor }}>
                            {item.toxicity_score}/10
                          </span>
                        </div>
                      </div>

                      <div className="flex-shrink-0 w-full" style={{ height: "1px", background: "hsl(0 0% 10%)" }} />

                      {/* ▸ Analysis Body */}
                      <div className="flex-1 px-5 py-4 overflow-hidden">
                        {item.analysis_logic && (
                          <div>
                            <div className="text-[9px] font-mono uppercase tracking-[0.2em] mb-3" style={{ color: "hsl(0 0% 38%)" }}>
                              FORENSIC ANALYSIS
                            </div>
                            <div className="py-3 px-4 rounded-sm" style={{ background: "hsl(0 0% 6%)", borderLeft: "2px solid hsl(184 80% 40%)" }}>
                              <p
                                className="text-xs leading-relaxed"
                                style={{
                                  color: "hsl(0 0% 58%)",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 6,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {item.analysis_logic}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex-shrink-0 w-full" style={{ height: "1px", background: "hsl(0 0% 10%)" }} />

                      {/* ▸ Locked CTA */}
                      <div className="flex-shrink-0 px-5 py-5 flex justify-center">
                        <div className="flex items-center gap-3">
                          <Lock className="w-4 h-4" style={{ color: "hsl(0 0% 35%)" }} />
                          <span className="text-[11px] font-mono uppercase tracking-[0.2em]" style={{ color: "hsl(0 0% 38%)" }}>
                            FULL RESPONSE LOCKED
                          </span>
                        </div>
                      </div>

                      <div className="flex-shrink-0 w-full" style={{ height: "1px", background: "hsl(0 0% 10%)" }} />

                      {/* ▸ Footer */}
                      <div className="flex-shrink-0 py-4 flex justify-center">
                        <span className="text-[9px] font-mono uppercase tracking-[0.25em] select-none" style={{ color: "hsl(0 0% 28%)" }}>
                          ANALYZE YOURS → GREYROCKAI.APP®
                        </span>
                      </div>
                    </div>

                    {/* ── ACTION BUTTONS ── */}
                    <div className="flex items-center justify-center gap-3">
                      {item.invideo_prompt && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(item.invideo_prompt!, "TikTok hook")}
                          className="gap-2 text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-cyan hover:bg-transparent transition-colors"
                        >
                          <Copy className="w-3.5 h-3.5" /> Copy Hook
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadTeaserCard(item, i)}
                        className="gap-2 text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-cyan hover:bg-transparent transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="pb-16 text-center" ref={analyzeRef}>
          <div className="rounded-xl border border-cyan/20 bg-cyan/5 p-8 md:p-12">
            <h2 className="text-xl md:text-2xl font-bold mb-3">Want to try the full tool?</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Paste any message. Get a forensic breakdown, drama score, and a Grey Rock response — instantly.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-lg bg-cyan text-charcoal-deep hover:opacity-90 transition-colors"
            >
              Open GreyRock AI® <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 mb-3">
          <img src={logo} alt="GreyRock AI®" className="w-6 h-6 object-contain opacity-60" />
          <span className="text-sm font-medium text-muted-foreground">GreyRockAI.app®</span>
        </div>
        <SocialLinks className="justify-center" />
        <div className="flex items-center justify-center gap-4 text-xs">
          <a href="/privacy" className="text-muted-foreground hover:text-cyan transition-colors">Privacy Policy</a>
          <span className="text-muted-foreground/40">•</span>
          <a href="/terms" className="text-muted-foreground hover:text-cyan transition-colors">Terms of Service</a>
        </div>
        <p className="text-sm text-muted-foreground">© 2026 GreyRock AI®. All Rights Reserved.</p>
      </footer>

      {/* Contact Modal */}
      {contactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setContactOpen(false)}>
          <div
            className="relative w-full max-w-md mx-4 rounded-xl border border-border bg-card p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setContactOpen(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-lg font-bold text-foreground mb-1">Creator Partnerships</h3>
            <p className="text-xs text-muted-foreground mb-5">We'll get back to you within 24 hours.</p>

            <form onSubmit={handleContactSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Name"
                value={contactForm.name}
                onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-cyan/40"
              />
              <input
                type="email"
                placeholder="Email"
                value={contactForm.email}
                onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-cyan/40"
              />
              <textarea
                placeholder="How can we help?"
                rows={4}
                value={contactForm.message}
                onChange={(e) => setContactForm((f) => ({ ...f, message: e.target.value }))}
                className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-cyan/40 resize-none"
              />
              <Button type="submit" disabled={sending} className="w-full bg-cyan text-charcoal-deep hover:bg-cyan/90 font-semibold">
                {sending ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorPortal;
